import {
  DropdownButton,
  DropdownButtonContext,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { AsyncStatus, ConversationsChannels } from 'lib-shared-legacy-types';
import { FC, useEffect, useState } from 'react';

import {
  findLabelByLanguageCode,
  LanguageCode,
} from 'app/utils/supportedLanguages';
import { requestAvailableSatisfactionLanguages } from 'components/conversation/api/satisfaction';

import { SatisfactionDropdown } from './components/SatisfactionDropdown';

export type SatisfactionButtonProps = {
  /** The channel. */
  channel: ConversationsChannels;
  /** The contact point. */
  contactPoint: string;
  /** The selected language code. */
  selected: LanguageCode;
  /** Whether or not it can be cancelled. */
  isCancellable: boolean;
  /** Invoked whenever the language is changed. */
  onChange: (language: LanguageCode) => void;
  /** Invoked whenever it is cancelled. */
  onCancel: () => void;
  /** Invoked whenever the dropdown is toggled. */
  onToggle?: (isOpen: boolean) => void;
};

export const SatisfactionButton: FC<SatisfactionButtonProps> = ({
  channel,
  contactPoint,
  selected,
  isCancellable,
  onToggle,
  onChange,
  onCancel,
}) => {
  const [languageCodes, setLanguageCodes] = useState<LanguageCode[]>([]);
  const [languageCodesAsyncStatus, setLanguageCodesAsyncStatus] =
    useState<AsyncStatus>('succeeded');

  useEffect(() => {
    setLanguageCodesAsyncStatus('loading');

    requestAvailableSatisfactionLanguages({
      channel,
      contactPoint,
    })
      .then((response) => {
        setLanguageCodes(response.data.languages);
        setLanguageCodesAsyncStatus('succeeded');
      })
      .catch(() => setLanguageCodesAsyncStatus('failed'));
  }, [channel, contactPoint]);

  // Default to English if the selected language is not supported
  const selectedLanguage = languageCodes.includes(selected) ? selected : 'en';

  return languageCodes.length > 0 ? (
    <Tooltip content={`CSAT ${findLabelByLanguageCode(selectedLanguage)}`}>
      <DropdownButton
        icon="smile"
        data-testid="csat-button"
        onToggle={(isOpen) => onToggle && onToggle(isOpen)}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown }) => (
              <SatisfactionDropdown
                selected={selectedLanguage}
                languageCodes={languageCodes}
                languageCodesAsyncStatus={languageCodesAsyncStatus}
                cancelAsyncStatus={'succeeded'}
                isCancellable={isCancellable}
                toggleDropdown={toggleDropdown}
                onChange={onChange}
                onCancel={onCancel}
              />
            )}
          </DropdownButtonContext.Consumer>
        }
      />
    </Tooltip>
  ) : null;
};
